import { ElmErrorProblem, ElmErrorMessages, ElmErrorFormattedMsg } from 'frontend/types/elmer';

export type WarningInputSource = 'asset' | 'camperdown' | 'settings';

export interface WarningPosition {
  line: number;
  column: number;
}

export interface WarningLocation {
  start: WarningPosition;
  end: WarningPosition;
}

export type TranspilerWarning =
  | AssetTranspilerWarning
  | CamperdownTranspilerWarning
  | SettingsTranspilerWarning;

export type Severity =
  | 'error' // blocks publishing and any attempt to compile
  | 'warning' // blocks publishing
  | 'info' // doesnt block anything
  | 'debug'; // ? not really using this at the moment

interface BaseTranspilerWarning {
  sourceType: WarningInputSource;
  problemType: string;
  message: string;
  severity: Severity;
}

export interface AssetTranspilerWarning extends BaseTranspilerWarning {
  sourceType: 'asset';
  alias: string;
}

export interface CamperdownTranspilerWarning extends BaseTranspilerWarning {
  sourceType: 'camperdown';
  location: WarningLocation;
}

export interface SettingsTranspilerWarning extends BaseTranspilerWarning {
  sourceType: 'settings';
  field: string;
}

/* This type is complicated because errors may come from various sources:
    - ElmErrorProblem is the best. It has metadata, and a lot of formatting info. From the compiler itself
    - ElmErrorFormattedMsg is from the Elm compiler, but typically "uh, syntax error, I'm lost".
    - string typically comes from us: there's a server error, something threw an exception. Who knows.
*/

export interface EditorWarning {
  message: string | ElmErrorFormattedMsg | ElmErrorProblem;
  severity: Severity;
}

export function editorWarning(
  message: string | ElmErrorFormattedMsg | ElmErrorProblem,
  severity: Severity = 'error',
): EditorWarning {
  return { message, severity };
}

export function editorWarningToPlainText(warning: EditorWarning): string {
  if (typeof warning.message === 'string') {
    return warning.message;
  }

  if ('string' in warning.message) {
    return warning.message.string;
  }

  // Extract out the sub-strings
  return warning.message.message
    .map(m => {
      return typeof m === 'string' ? m : m.string;
    })
    .join(' ');
}

export function editorWarningToStyledTree(warning: EditorWarning): HTMLElement {
  if (process.env.VUE_ENV === 'server' || typeof window === 'undefined') {
    // This whole function isn't SSR-safe, so abort if we need to
    return new HTMLElement();
  }

  const rootPre = document.createElement('pre');
  rootPre.classList.add('elm-error');

  if (typeof warning.message === 'string') {
    rootPre.innerHTML = warning.message;
    return rootPre;
  }

  if ('string' in warning.message) {
    rootPre.innerHTML = warning.message.string;
    return rootPre;
  }

  return elmErrorProblemToStyledTree(warning.message);
}

export function elmErrorProblemToStyledTree(error: ElmErrorProblem): HTMLElement {
  if (process.env.VUE_ENV === 'server' || typeof window === 'undefined') {
    // This whole function isn't SSR-safe, so abort if we need to
    return new HTMLElement();
  }

  const rootPre = document.createElement('pre');
  rootPre.classList.add('elm-error');

  const title = document.createElement('h3');
  title.textContent = error.title;
  rootPre.appendChild(title);

  error.message.forEach(m => {
    const span = document.createElement('span');

    if (typeof m === 'string') {
      span.textContent = m;
      rootPre.appendChild(span);
      return;
    }
    span.textContent = m.string;

    if (m.bold) {
      span.style.fontWeight = 'bold';
    }
    if (m.underline) {
      span.style.textDecoration = 'underline';
    }
    if (m.color) {
      span.style.color = m.color === 'yellow' ? 'brown' : m.color; // yellow does not render well
    }
    rootPre.appendChild(span);
  });

  return rootPre;
}

export interface EditorWarnings {
  [key: string]: EditorWarning[];
}
export interface EditorWarningPayload {
  assetAlias: string;
  warnings: EditorWarning[];
}

export type TranspilerWarningGetter = (assetAlias: string) => TranspilerWarning[];
export type WarningCountsGetter = (sourceType: WarningInputSource, severity: Severity) => number;
export type AssetWarningCountsGetter = (assetAlias: string, severity: Severity) => number;
export type EditorWarningGetter = (assetAlias: string) => EditorWarning[];
